<template>
    <!-- Grouped Benefits View -->
    <article
        class="w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-100 dark:border-slate-700"
    >
        <div class="flex items-start gap-3 p-4">
            <!-- Card/Benefit Icon -->
            <div
                class="relative w-12 h-8 shrink-0"
                :class="{ 'opacity-50': cardGroup.isLocked && !isExpanded }"
            >
                <div class="relative w-full h-full">
                    <!-- Card Image with Stack Effect -->
                    <div
                        v-if="cardGroup.benefits.length > 1"
                        class="absolute w-[80%] h-full rounded-sm bg-slate-200 -bottom-0.5 left-1/2 z-0 -translate-x-1/2"
                    ></div>
                    <div
                        v-if="cardGroup.benefits.length > 1"
                        class="absolute w-[90%] h-full rounded-sm bg-slate-300 -bottom-0.5 left-1/2 z-10 -translate-x-1/2"
                    ></div>

                    <!-- Main card image -->
                    <ImageLoader class="relative z-20 w-full h-full">
                        <template #image>
                            <img
                                class="w-full h-full rounded-sm border border-slate-200 dark:border-slate-700 object-cover relative z-20"
                                :src="`${cdnUrl}/${cardGroup?.card?.image?.path}/_small/${cardGroup?.card?.image?.file_name}`"
                                :alt="`${cardGroup?.card?.title || 'Card'} image`"
                            />
                        </template>
                        <template #error>
                            <div
                                class="flex items-center justify-center w-full h-full rounded-sm bg-slate-200"
                            >
                                <Icon icon="credit-card" class="h-4 w-4" />
                            </div>
                        </template>
                    </ImageLoader>
                </div>

                <!-- Benefits count badge -->
                <div
                    v-if="cardGroup.benefits.length > 1"
                    class="absolute -right-2 -top-2 bg-slate-800 text-white text-xs font-medium rounded-full h-5 min-w-[20px] px-1.5 flex items-center justify-center z-20"
                >
                    {{ cardGroup.benefits.length }}
                </div>
            </div>

            <!-- Group Info -->
            <div
                class="flex-1 min-h-8 min-w-0"
                :class="{ 'opacity-50': cardGroup.isLocked && !isExpanded }"
            >
                <h3 class="text-slate-800 dark:text-slate-100 text-base font-medium leading-tight">
                    <span class="block">
                        <span class="line-clamp-2 break-words">
                            {{ cardGroup.card?.title || 'Unknown Card' }}
                            <span
                                v-if="
                                    cardGroup?.connected_account_last_four &&
                                    showLastFour
                                "
                                class="text-slate-500 dark:text-slate-400 text-sm font-normal"
                            >
                                ({{ cardGroup?.connected_account_last_four }})
                            </span>
                        </span>
                    </span>
                </h3>
                <!-- Badge: Up to $X/year -->
                <div class="py-1 px-2 bg-slate-100 dark:bg-slate-700 rounded-lg w-fit mt-1">
                    <p class="text-slate-500 dark:text-slate-400 font-medium text-sm">
                        Up to {{ formattedTotalAmount }}/year
                    </p>
                </div>
            </div>

            <!-- Expand/Collapse Button -->
            <button
                class="btn-base btn-icon"
                @click="toggleExpand"
                aria-label="Toggle benefit group"
            >
                <Icon
                    :icon="isExpanded ? 'chevron-up' : 'chevron-down'"
                    class="w-5 h-5"
                />
            </button>
        </div>

        <!-- Usage Summary for locked/unlocked -->
        <div
            class="px-4 pb-2"
            :class="{ 'opacity-50': cardGroup.isLocked && !isExpanded }"
        >
            <div class="flex items-center text-sm text-slate-800 dark:text-slate-100 font-normal">
                <span>{{ formattedUsedAmount }}</span>
                <span class="ml-1">of {{ formattedTotalAmount }}</span>
            </div>
            <!-- Usage Bar -->
            <div class="mt-1.5">
                <div
                    class="w-full rounded-full h-1 relative"
                    :class="
                        cardGroup.isLocked ? 'bg-slate-200' : 'bg-orange-200'
                    "
                >
                    <div
                        v-if="!cardGroup.isLocked"
                        class="bg-10x-orange h-1 rounded-full transition-all"
                        :style="{ width: `${calculatedUsagePercentage}%` }"
                    ></div>
                    <!-- 100% Completion Indicator -->
                    <div
                        v-if="
                            !cardGroup.isLocked &&
                            calculatedUsagePercentage >= 100
                        "
                        class="absolute -right-1 top-1/2 -translate-y-1/2 w-4 h-4 bg-10x-orange rounded-full flex items-center justify-center border-2 border-white"
                    >
                        <Icon
                            icon="check"
                            svgClass="w-2 h-2 text-white"
                            class="text-white"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- Upgrade to track button for locked groups (only when collapsed) -->
        <div v-if="cardGroup.isLocked && !isExpanded" class="px-4 pb-4">
            <button
                class="flex w-full items-center justify-center gap-2 py-3 px-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer"
                @click="openUpgradeModal"
            >
                <Icon icon="lock" class="w-4 h-4 text-slate-500 dark:text-slate-400" />
                <span>Upgrade to track</span>
            </button>
        </div>

        <!-- Expanded Benefits -->
        <div
            v-if="isExpanded"
            class="divide-y-2 divide-slate-100 pl-11 pr-4 pb-1"
        >
            <div
                v-for="(benefit, idx) in cardGroup.benefits"
                :key="`${benefit.benefit.id}-${benefit.connected_account_id}`"
                class="relative pt-4 pb-1"
            >
                <!-- Tree SVG Connectors for grouped items -->
                <div class="absolute w-16 -left-16 -top-2 bottom-0 h-20">
                    <svg
                        v-if="idx === 0"
                        width="12"
                        height="75"
                        viewBox="0 0 12 75"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="absolute -top-4 left-12"
                    >
                        <path
                            d="M1.5 5V53Q1.5 66 12 66"
                            stroke="#CBD5E1"
                            stroke-width="1"
                            stroke-linecap="round"
                        />
                    </svg>
                    <svg
                        v-else
                        width="12"
                        height="190"
                        viewBox="0 0 12 190"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="absolute -top-24 left-12"
                    >
                        <path
                            d="M1.5 0V137Q1.5 150 12 150"
                            stroke="#CBD5E1"
                            stroke-width="1"
                            stroke-linecap="round"
                        />
                    </svg>
                </div>

                <BenefitsTableMobileCard
                    :benefit="benefit"
                    :index="idx"
                    :hide-upgrade-button="true"
                    @add-usage="handleAddUsage"
                    @view-details="handleViewDetails"
                    class="gap-4 flex flex-col"
                />
            </div>
        </div>

        <!-- Upgrade to track button for locked groups (when expanded) -->
        <div v-if="cardGroup.isLocked && isExpanded" class="px-4 pb-4">
            <button
                class="flex w-full items-center justify-center gap-2 py-3 px-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer"
                @click="openUpgradeModal"
            >
                <Icon icon="lock" class="w-4 h-4 text-slate-500 dark:text-slate-400" />
                <span>Upgrade to track</span>
            </button>
        </div>
    </article>
</template>

<script setup>
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import { parseLocalDate } from '@/utils/dateUtils';
import BenefitsTableMobileCard from '@/components/card-benefits/benefits-table-mobile-card.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import Icon from '@/components/ui/icon/icon.vue';
import Filters from '@/filters';

const props = defineProps({
    cardGroup: {
        type: Object,
        required: true,
    },
    index: {
        type: Number,
        default: 0,
    },
});

const emit = defineEmits(['add-usage', 'view-details']);

const isExpanded = ref(false);
const cdnUrl = ref(window.cdnUrl);
const store = useCardBenefitsStore();
const { showLastFour } = storeToRefs(store);

const calculatedTotalAnnualValue = computed(() => {
    let total = 0;
    const { start: ytdStart, end: ytdEnd } = store.periodBoundaries.annual;

    // Get live benefits from store to ensure reactivity to optimistic updates
    const liveBenefits = props.cardGroup.benefits.map((b) => {
        return (
            store.benefits.find(
                (sb) =>
                    sb.benefit.id === b.benefit.id &&
                    sb.connected_account_id === b.connected_account_id
            ) || b
        );
    });

    for (const benefitItem of liveBenefits) {
        const benefit = benefitItem.benefit;
        if (!benefit) continue;

        // Skip count-based and NCRED (non-dollar) benefits
        if (store.checkIsCountBased(benefit)) {
            continue;
        }

        // Skip multi-year benefits that were used in a previous year
        if (!store.isBenefitActiveThisYear(benefitItem, ytdStart, ytdEnd)) {
            continue;
        }

        // Get the display amount
        const displayAmount = store.calculateDisplayAmount(benefitItem);

        // Special handling for monthly benefits with override
        const count = parseFloat(benefit.value_details?.count) || 1;
        let annualValue;

        if (
            benefit.period_type?.toUpperCase() === 'MONTHLY' &&
            benefit.value_details?.override_month
        ) {
            // Calculate: (base * 11 months) + (override * 1 month)
            // The override REPLACES the base amount for that specific month
            const baseAmount = parseFloat(benefit.amount) || 0;
            const overrideValue =
                parseFloat(benefit.value_details.override_month.value) || 0;

            annualValue = baseAmount * count * 11 + overrideValue * count;
        } else {
            // Convert to annual value based on period type
            annualValue = store.convertPeriodValueToAnnual(
                displayAmount,
                benefit.period_type,
                count
            );
        }

        total += annualValue;
    }

    return total;
});

// Check if all benefits are purely count-based (count but no dollar value)
const allPurelyCountBased = computed(() => {
    return props.cardGroup.benefits.every((benefit) => {
        const summary = benefit.benefit?.values_summary;
        return (
            summary?.currency === 'NCRED' ||
            (summary?.is_count_based && !parseFloat(benefit.benefit?.amount || 0))
        );
    });
});

// Calculate total YTD usage across all benefits in the group
const calculatedYtdUsage = computed(() => {
    const { start: ytdStart, end: ytdEnd } = store.periodBoundaries.annual;

    return props.cardGroup.benefits.reduce((sum, benefit) => {
        // Get live benefit from store to ensure reactivity to optimistic updates
        const liveBenefit =
            store.benefits.find(
                (sb) =>
                    sb.benefit.id === benefit.benefit.id &&
                    sb.connected_account_id === benefit.connected_account_id
            ) || benefit;

        // Sum usage records within YTD period
        const usageRecords = liveBenefit.usage_records || [];

        if (allPurelyCountBased.value) {
            // For purely count-based benefits, sum the usage_count from records
            const ytdCount = usageRecords.reduce((total, record) => {
                const usedAt = parseLocalDate(record.used_at);
                if (usedAt >= ytdStart && usedAt <= ytdEnd) {
                    return total + (parseFloat(record.usage_count) || 0);
                }
                return total;
            }, 0);
            return sum + ytdCount;
        } else {
            // Skip count-based and NCRED benefits in mixed groups
            if (store.checkIsCountBased(liveBenefit.benefit)) {
                return sum;
            }
            // For monetary benefits (including count × value), sum the dollar amounts
            const ytdUsage = usageRecords.reduce((total, record) => {
                const usedAt = parseLocalDate(record.used_at);
                if (usedAt >= ytdStart && usedAt <= ytdEnd) {
                    return total + parseFloat(record.total_amount_used || 0);
                }
                return total;
            }, 0);
            return sum + ytdUsage;
        }
    }, 0);
});

const calculatedUsagePercentage = computed(() => {
    const totalUsedYtd = calculatedYtdUsage.value;

    if (allPurelyCountBased.value) {
        // For purely count-based benefits, calculate total available count
        const totalAvailableCount = props.cardGroup.benefits.reduce(
            (sum, benefit) => {
                const maxCount = benefit.benefit?.value_details?.count || 0;
                return sum + maxCount;
            },
            0
        );

        if (totalAvailableCount === 0) return 0;

        const percentage = (totalUsedYtd / totalAvailableCount) * 100;
        if (isNaN(percentage)) return 0;

        return Math.min(Math.round(percentage), 100);
    } else {
        // For monetary benefits, use YTD usage against annual value
        const totalAvailable = calculatedTotalAnnualValue.value;

        if (totalAvailable === 0) return 0;

        const percentage = (totalUsedYtd / totalAvailable) * 100;
        if (isNaN(percentage)) return 0;

        return Math.min(Math.round(percentage), 100);
    }
});

const formattedTotalAmount = computed(() => {
    if (allPurelyCountBased.value) {
        // For purely count-based benefits, sum the available counts
        const totalCount = props.cardGroup.benefits.reduce((sum, benefit) => {
            const count =
                parseFloat(benefit.benefit?.value_details?.count) || 0;
            return sum + count;
        }, 0);
        return totalCount.toString();
    } else {
        // For monetary benefits, format as money
        const total = calculatedTotalAnnualValue.value;
        return total > 0
            ? Filters.formatSmartMoney(total)
            : Filters.formatSmartMoney(0);
    }
});

const toggleExpand = () => {
    isExpanded.value = !isExpanded.value;
};

const formatBenefitType = (benefitType) => {
    if (!benefitType) return 'Unknown Benefit';
    return benefitType
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase());
};

const formattedUsedAmount = computed(() => {
    if (allPurelyCountBased.value) {
        return calculatedYtdUsage.value.toString();
    }
    return Filters.formatSmartMoney(calculatedYtdUsage.value);
});

const openUpgradeModal = () => {
    store.setShowUpgradeModal(true);
};

const handleAddUsage = (benefit) => {
    emit('add-usage', benefit);
};

const handleViewDetails = (benefit) => {
    emit('view-details', benefit);
};
</script>
